import { useCallback, useState, FC, useEffect, Fragment } from "react";
import Accordion, { AccordionItem } from "../../../components/bootstrap/Accordion";
import Button from "../../../components/bootstrap/Button";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../components/bootstrap/Modal";
import Spinner from "../../../components/bootstrap/Spinner";
import Checks from "../../../components/bootstrap/forms/Checks";
import { Permission, PermissionGroup, PermissionsApiResponse, RolePermission } from "../../../type/role-type";
import useFetch from "../../../hooks/useFetch";
import { PermissionService } from "../../../services/auth/permissionService";
import { UserService } from "../../../services/users/userService";
import { UserApiResponse } from "../../../type/user-type";
import { toast } from 'react-toastify';
import ErrorMessage from "../../../components/ErrorMessage";
import IconWithTooltip from "../../../components/IconWithTooltip";

interface RoleCreateModalProps {
	isOpen: boolean;
	setIsOpen(...args: unknown[]): unknown;
	userPermissions: RolePermission[];
	newPermissions: RolePermission[] | null;
	setNewPermissions: (permissions: RolePermission[]) => void;
	userId: string;
	companyId: string;
}

const RoleCreateModal: FC<RoleCreateModalProps> = ({ isOpen, setIsOpen, userPermissions, userId, setNewPermissions, newPermissions, companyId }) => {

	const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState<number[]>([]);
	const [updating, setUpdating] = useState<boolean>(false);
	const [resetting, setResetting] = useState<boolean>(false);

	const [permissionsData, permissionsLoading, error] = useFetch(useCallback(async () => {
		const permissionService = new PermissionService();
		const response = await permissionService.getPermissions();
		return response.getResponseData() as PermissionsApiResponse;
	}, []));

	const updatePermissions = async () => {
		try {
			setUpdating(true);
			let response = await (await (new UserService()).editUserPermissions(userId, companyId, selectedPermissions)).getResponseData() as UserApiResponse;
			if (response.success && response.data) {
				setNewPermissions(response.data.userPermissions);
				toast.success(response.message);
				setSelectedPermissions([]);
			} else {
				toast.error(response.message);
			}
		} catch (error: any) {
			setIsOpen(false);
			toast.error('Error al Actualizar Permisos');
		} finally {
			setUpdating(false);
			setIsOpen(false);
		}
	};

	const resetPermissions = async () => {
		try {
			setResetting(true);
			let response = await (await (new UserService()).resetUserPermissions(userId, companyId)).getResponseData() as UserApiResponse;
			if (response.success && response.data) {
				toast.success('Permisos reseteados a los permisos por defecto del rol');
			} else {
				toast.error(response.message);
			}
		} catch (error: any) {
			setIsOpen(false);
			toast.error('Error al actualizar los permisos');
		} finally {
			setIsOpen(false);
			setResetting(false);
		}
	};

	useEffect(() => {
		newPermissions
			? setSelectedPermissions(newPermissions.map((permission: RolePermission) => permission.permission.id))
			: setSelectedPermissions(userPermissions.map((permission: RolePermission) => permission.permission.id));
	}, [newPermissions, userPermissions]);

	const getPermissionsLabel = (id: string, label: string, description: string) => {
		return (
			<>

				<label htmlFor={id}>{label}</label>
				<IconWithTooltip icon="Info" label={description} />
			</>
		)
	};

	const getContent = () => {
		if (permissionsLoading) return <div className="text-center"><Spinner /></div>;

		if (error) return <ErrorMessage message={error} />;

		return permissionsData?.map((group: PermissionGroup, index: number) => {
			return (
				<div className="col-lg-3 col-md-6 col-sm-6 mb-5" key={group.id}>
					<Accordion id={group.name} isFlush activeItemId={group.id}>
						<AccordionItem id={group.id} title={group.label}>
							<>
								<Checks
									label="Seleccionar todos"
									value="true"
									checked={selectAll.includes(group.id)}
									onChange={() => {
										const list = group.permissions.map((item: Permission) => item.id);
										if (selectAll.includes(group.id)) {
											setSelectAll(selectAll.filter((id: number) => id !== group.id));
											setSelectedPermissions(selectedPermissions.filter(item => !list.includes(item)));
										} else {
											setSelectAll([...selectAll, group.id]);
											setSelectedPermissions([...selectedPermissions.concat(list)]);
										}
									}}
								/>
							</>
							{group.permissions.map((permission: Permission) => {
								return (
									<div key={permission.id}>
										<Checks
											id={permission.id.toString()}
											label={getPermissionsLabel(permission.id.toString(), permission.label, permission.description)}
											value={permission.id}
											checked={selectedPermissions.includes(permission.id)}
											onChange={() => {
												selectedPermissions.includes(permission.id)
													? setSelectedPermissions(selectedPermissions.filter((id: number) => id !== permission.id))
													: setSelectedPermissions([...selectedPermissions, permission.id])
											}}
										/>
									</div>
								);
							})}
						</AccordionItem>
					</Accordion>
				</div>
			);
		});
	}

	return (
		<Modal isOpen={isOpen} setIsOpen={setIsOpen} size='xl' titleId='Nuevo Rol'>
			<ModalHeader setIsOpen={setIsOpen} className='p-4'>
				<ModalTitle id='new_role'>Editar permisos</ModalTitle>
			</ModalHeader>
			<ModalBody className='px-4'>
				<div className="row">
					{getContent()}
				</div>
			</ModalBody>
			<ModalFooter className='px-4 pb-4 d-flex justify-content-between'>
				<Button icon="RestartAlt" color='secondary' onClick={resetPermissions}>
					{resetting ? (<Fragment> <Spinner isSmall /> Reseteando... </Fragment>) : 'Resetear Permisos'}
				</Button>
				<Button isDisable={updating} icon={updating ? '' : 'Save'} color='primary' onClick={updatePermissions}>
					{updating ? (<Fragment> <Spinner isSmall /> Actualizando... </Fragment>) : 'Actualizar'}
				</Button>
			</ModalFooter>
		</Modal>
	)
}

export default RoleCreateModal;